import {HttpClient, HttpErrorResponse, HttpHeaders, HttpParams} from "@angular/common/http";
import {inject, Injectable} from "@angular/core";
import {Observable, of, Subject, throwError} from "rxjs";
import {catchError, finalize, map, switchMap, takeUntil, tap} from "rxjs/operators";
import {environment} from "src/environments/environment";
import {AllTeamIdAndOrgId} from "../models/custom-models/all-team-id-and-org-id";
import {StatsRequest, StatsRequestForTaskMaster} from "../models/stats-request";
import {
    BulkDeleteTaskOrAskStatusRequest,
    PersonalCreateTaskRequest,
    PersonalUpdateTaskRequest,
    SetCurrentlyActiveTaskRequest,
    SubTaskRequestModel,
    TaskRequest,
    UpdateBugDetailsRequest,
    UpdateTaskFieldsInBulkRequest,
    UpdateTaskFieldsInBulkSprintRequest,
    UpdateTaskWithSprintResponse,
} from "../modules/task-management/models/task-request";
import {
    BulkDeleteTasksResponse,
    EpicsForListingResponse,
    GetTaskResponse,
    PersonalTaskResponse,
    ScheduledTasksRequest,
    ScheduledTasksResponse,
    TaskResponse,
    UploadAttachmentTaskResponse,
    ValidatedWorkItemResponse,
    ValidateWorkItemRelation,
} from "../modules/task-management/models/task-response";
import {UserDetails} from "../models/userDetails";
import {
    TaskHistory,
    TaskHistoryMappingFields,
    UpdatedTaskPreviewResponse,
} from "../modules/task-management/update-task/models/update-task-model.model";
import {APIResponse} from "../models/Response";
import {AttachmentDownloadInfo} from "../models/attachment-download-info";
import {AttachmentThumbnailMetadata} from "../models/attachment-thumbnail-metadata";
import {AttachmentDownloadService} from "./attachment-download.service";
import {GetTaskByFilterResponse} from "../models/stats-response";
import {TaskMasterTasksListObject} from "../models/custom-models/task-master-tasks-list-object";
import {
    GetAllEnvironmentSeverityResolution,
    OpenTaskRequestModel,
    WorkItemDeleteReasonsRequest,
} from "../modules/shared/Models/task.model";
import {SearchCriteria} from "../modules/shared/Models/shared-data-models.model";
import {OrganizationDashboardStructure} from "../models/custom-models/org-buproject-team-structure";
import {MatDialog} from "@angular/material/dialog";
import {ToastrService} from "ngx-toastr";
import {SharedService} from "./shared.service";
import {overallSearchMeetingResponse} from "../modules/meetings-workflow/models/meeting.model";
import {SecureStorageService} from "../modules/core/services/secure-storage.service";
import {LocalStorageRelatedData} from "../local-storage-related-data.model";
import {FileUploadTimeoutConstants} from "../modules/task-management/models/file-upload.model";

@Injectable({
    providedIn: "root",
})
export class TaskService {
    apiServerUrl = environment.apiBaseUrl;
    allowedWorkflowTypeIds = [1, 3, 6];
    private cancelPendingRequests$ = new Subject();
    private storedViewTaskApiResponse: OrganizationDashboardStructure[] | null = null;
    private secureStorageService = inject(SecureStorageService)

    constructor(
        private http: HttpClient,
        private dialog: MatDialog,
        private toastr: ToastrService,
        private sharedService: SharedService,
        private attachmentDownloadService: AttachmentDownloadService,
    ) {
    }

    //  create a task
    createTask(task: TaskRequest, userAccountId: number): Observable<TaskResponse> {
        return this.http
            .post<TaskResponse>(`${this.apiServerUrl}/task/addTask`, task, {
                headers: {
                    accountIds: userAccountId.toString(),
                },
            })
            .pipe(
                catchError((err: HttpErrorResponse) => {
                    return throwError(() => err);
                }),
            );
    }

    //  get all users for assigning task
    getAllUsersToAssignTask(orgId: number, teamId: number): Observable<APIResponse<UserDetails[]>> {
        return this.http
            .get<APIResponse<UserDetails[]>>(`${this.apiServerUrl}/task/getAllUsersToAssignTask/${orgId}/${teamId}`)
            .pipe(
                catchError((error) => {
                    return throwError(()=>error);
                }),
            );
    }

    //  get all users by all teams and all orgs
    getUsersByAllTeamsAndOrgs(allTeamIdAndOrgId: AllTeamIdAndOrgId): Observable<APIResponse<UserDetails[]>> {
        const userAllAccountIds = this.sharedService.getAllAccountIdsOfLoggedInUser() as string;
        return this.http
            .post<APIResponse<UserDetails[]>>(`${this.apiServerUrl}/task/getUsersByAllTeamAndOrg`, allTeamIdAndOrgId, {
                headers: {
                    accountIds: userAllAccountIds,
                },
            })
            .pipe(
                catchError((error) => {
                    return throwError(()=>error);
                }),
            );
    }

    // get the task by taskNumber
    getTaskByTaskNumber(taskNumber: number): Observable<APIResponse<GetTaskResponse>> {
        return this.http.get<APIResponse<GetTaskResponse>>(`${this.apiServerUrl}/task/getTask/${taskNumber}`).pipe(
            catchError((error) => {
                return throwError(()=>error);
            }),
        );
    }

    getTaskByTaskNumberV2(
        openTaskRequestModel: OpenTaskRequestModel,
        accountIds: string,
    ): Observable<APIResponse<GetTaskResponse>> {
        return this.http
            .post<APIResponse<GetTaskResponse>>(`${this.apiServerUrl}/task/getTask`, openTaskRequestModel, {
                headers: {
                    accountIds: accountIds,
                },
            })
            .pipe(
                catchError((error) => {
                    return throwError(()=>error);
                }),
            );
    }

    // update the task
    updateTask(
        task: TaskResponse,
        taskId: number,
        currentUserAccountId: number,
    ): Observable<APIResponse<TaskResponse>> {
        let headers = {
            accountIds: currentUserAccountId.toString(),
        };
        return this.http
            .post<APIResponse<TaskResponse>>(`${this.apiServerUrl}/task/updateTask/${taskId}`, task, {
                headers: headers,
            })
            .pipe(
                catchError((error) => {
                    return throwError(()=>error);
                }),
            );
    }

    referenceMeetingEffortsHandling(
        taskId: number,
        actionId: number,
        accountIdAssignedTo: number | null = null
    ): Observable<any> {
        const payload = {
            taskId,
            accountIdAssignedTo,
            actionId
        };

        return this.http
            .post(`${this.apiServerUrl}/meeting/notifyAndPutAttendeesEffort`, payload)
            .pipe(
                catchError((error) => throwError(() => error))
            );
    }


    //  get all users for mentoring task
    getAllUsersToMentorTask(orgId: number, teamId: number): Observable<APIResponse<UserDetails[]>> {
        return this.http
            .get<APIResponse<UserDetails[]>>(`${this.apiServerUrl}/task/getAllUsersToMentorTask/${orgId}/${teamId}`)
            .pipe(
                catchError((error) => {
                    return throwError(()=>error);
                }),
            );
    }

    //  get all users for observing task
    getAllUsersToObserveTask(orgId: number, teamId: number): Observable<APIResponse<UserDetails[]>> {
        return this.http
            .get<APIResponse<UserDetails[]>>(`${this.apiServerUrl}/task/getAllUsersToObserveTask/${orgId}/${teamId}`)
            .pipe(
                catchError((error) => {
                    return throwError(()=>error);
                }),
            );
    }

    searchTasksByString(
        searchRequestObj: SearchCriteria,
        accountIds: string,
    ): Observable<APIResponse<TaskMasterTasksListObject[]>> {
        const headers = {
            accountIds: accountIds,
        };
        return this.http
            .post<APIResponse<TaskMasterTasksListObject[]>>(`${this.apiServerUrl}/task/searchTasks`, searchRequestObj, {
                headers: headers,
            })
            .pipe(
                catchError((error) => {
                    return throwError(()=>error);
                }),
            );
    }

    searchMeetingsByString(
        searchQuery: string,
        accountIds: string,
    ): Observable<APIResponse<overallSearchMeetingResponse[]>> {
        const headers = {
            accountIds: accountIds,
        };
        const body = {
            searchTerm: searchQuery,
        };
        return this.http
            .post<APIResponse<overallSearchMeetingResponse[]>>(`${this.apiServerUrl}/meeting/searchMeeting`, body, {
                headers: headers,
            })
            .pipe(
                catchError((error) => {
                    return throwError(()=>error);
                }),
            );
    }

    //  get task of a team with filters or without filters
    getFilteredTaskOfTeam(
        statsRequest: StatsRequest,
        pageNumber: number = 0,
        pageSize: number = 25,
    ): Observable<APIResponse<GetTaskByFilterResponse>> {
        return this.http
            .post<APIResponse<GetTaskByFilterResponse>>(
                `${this.apiServerUrl}/task/getTaskByFilter?pageNumber=${pageNumber}&pageSize=${pageSize}`,
                statsRequest,
            )
            .pipe(
                catchError((error) => {
                    return throwError(()=>error);
                }),
            );
    }

    getTaskMasterTaskByGetTaskByFilter(
        statsRequest: StatsRequestForTaskMaster,
        pageNumber: number = 0,
        pageSize: number = 25,
        accountIds: string,
    ): Observable<APIResponse<GetTaskByFilterResponse>> {
        return this.http
            .post<APIResponse<GetTaskByFilterResponse>>(
                `${this.apiServerUrl}/task/getTaskByFilter?pageNumber=${pageNumber}&pageSize=${pageSize}`,
                statsRequest,
                {
                    headers: {
                        accountIds: accountIds,
                    },
                },
            )
            .pipe(
                catchError((error) => {
                    return throwError(()=>error);
                }),
            );
    }

    //  to get all the immediate attention from users of a task
    getAllImmediateAttentionFromUsers(orgId: number, teamId: number): Observable<APIResponse<UserDetails[]>> {
        return this.http
            .get<APIResponse<UserDetails[]>>(
                `${this.apiServerUrl}/task/getAllImmediateAttentionUsers/${orgId}/${teamId}`,
            )
            .pipe(
                catchError((error) => {
                    return throwError(()=>error);
                }),
            );
    }

    uploadAttachmentsInTask(
        files: File[],
        taskId: number,
        userAccountId: number,
    ): Observable<APIResponse<UploadAttachmentTaskResponse>> {
        let formData = new FormData();
        files.forEach((file) => {
            formData.append("files", file);
        });
        return this.uploadAttachment(formData, taskId, userAccountId);
    }

    uploadPersonalTaskAttachmentsInTask(
        files: File[],
        taskId: number,
        userAccountId: number,
    ): Observable<APIResponse<UploadAttachmentTaskResponse>> {
        let formData = new FormData();
        files.forEach((file) => {
            formData.append("files", file);
        });
        return this.uploadPersonalTaskAttachment(formData, taskId, userAccountId);
    }

    uploadAttachment(
        formData: FormData,
        taskId: number,
        uploaderAccountId: number,
    ): Observable<APIResponse<UploadAttachmentTaskResponse>> {
        let toastId: number;
        if (this.toastr) {
            let uploadToast = this.toastr.info("Scanning and Uploading...", "File Upload", {
                disableTimeOut: true,
                tapToDismiss: false,
            });
            toastId = uploadToast.toastId;
        }

        let params = new HttpParams();
        params = params.append("taskId", taskId);
        params = params.append("uploaderAccountId", uploaderAccountId);

        const uploadedFiles = formData.getAll("files") as File[];
        const totalBytes = uploadedFiles.reduce((sum, file) => sum + file.size, 0);
        const headers = new HttpHeaders({
            timeout: String(FileUploadTimeoutConstants.forPayload(totalBytes)),
        });

        return this.http
            .post<APIResponse<UploadAttachmentTaskResponse>>(
                `${this.apiServerUrl}/task-attachment/upload-attachments`,
                formData,
                {params: params, headers: headers},
            )
            .pipe(
                catchError((error) => {
                    return throwError(()=>error);
                }),
                finalize(() => {
                    if (this.toastr && toastId) {
                        this.toastr.clear(toastId);
                    } else if (this.toastr) {
                        this.toastr.clear();
                    }
                }),
            );
    }

    /**
     * Downloads a personal-task attachment (PT-16085 F6). The endpoint now returns download
     * metadata with a presigned S3 URL instead of raw bytes; AttachmentDownloadService turns it
     * into a Blob (plain S3 GET, expired-URL retry, legacy stream fallback), so the caller keeps
     * receiving a Blob exactly as before.
     */
    downloadPersonalTaskAttachment(personalTaskId: number, fileName: string, accountId: string): Observable<Blob> {
        return this.getPersonalTaskAttachmentDownloadInfo(personalTaskId, fileName, accountId).pipe(
            switchMap((info) =>
                this.attachmentDownloadService.fetchBlob(
                    info,
                    () => this.streamPersonalTaskAttachment(personalTaskId, fileName, accountId),
                    () => this.getPersonalTaskAttachmentDownloadInfo(personalTaskId, fileName, accountId),
                ),
            ),
        );
    }

    /** Fetches the download metadata {fileName, fileType, fileSize, downloadUrl} for one personal-task attachment. */
    getPersonalTaskAttachmentDownloadInfo(personalTaskId: number, fileName: string, accountId: string): Observable<AttachmentDownloadInfo> {
        let params = new HttpParams();
        params = params.append("personalTaskId", personalTaskId);
        params = params.append("fileName", fileName);
        return this.http
            .get<APIResponse<AttachmentDownloadInfo>>(`${this.apiServerUrl}/personal-task/download-attachment`, {
                params: params,
                headers: {
                    accountIds: accountId,
                },
            })
            .pipe(
                map((response) => response.data),
                catchError((error) => {
                    return throwError(() => error);
                }),
            );
    }

    /**
     * Legacy fallback (PT-16085 G3): streams the bytes of a personal-task attachment still stored
     * in the database. Only called when downloadUrl is null; removed together with backend P16.
     */
    private streamPersonalTaskAttachment(personalTaskId: number, fileName: string, accountId: string): Observable<Blob> {
        let params = new HttpParams();
        params = params.append("personalTaskId", personalTaskId);
        params = params.append("fileName", fileName);
        return this.http
            .get(`${this.apiServerUrl}/personal-task/stream-attachment`, {
                params: params,
                observe: "body",
                responseType: "blob",
                headers: {
                    accountIds: accountId,
                },
            })
            .pipe(
                catchError((error) => {
                    return throwError(() => error);
                }),
            );
    }

    /**
     * Downloads a task/comment attachment (PT-16085 F2). The endpoint now returns download
     * metadata with a presigned S3 URL instead of raw bytes; AttachmentDownloadService turns it
     * into a Blob (plain S3 GET, expired-URL retry, legacy stream fallback), so callers keep
     * receiving a Blob exactly as before.
     *
     * accountId MUST be the caller's single account in the task's org (G8): the backend resolves
     * the caller from the accountIds header and rejects a multi-id header (caller becomes 0).
     */
    downloadAttachment(taskId: number, fileName: string, accountId: string): Observable<Blob> {
        return this.getAttachmentDownloadInfo(taskId, fileName, accountId).pipe(
            switchMap((info) =>
                this.attachmentDownloadService.fetchBlob(
                    info,
                    () => this.streamTaskAttachment(taskId, fileName, accountId),
                    () => this.getAttachmentDownloadInfo(taskId, fileName, accountId),
                ),
            ),
        );
    }

    /** Fetches the download metadata {fileName, fileType, fileSize, downloadUrl} for one attachment. */
    getAttachmentDownloadInfo(taskId: number, fileName: string, accountId: string): Observable<AttachmentDownloadInfo> {
        let params = new HttpParams();
        params = params.append("taskId", taskId);
        params = params.append("fileName", fileName);
        return this.http
            .get<APIResponse<AttachmentDownloadInfo>>(`${this.apiServerUrl}/task-attachment/download-attachment`, {
                params: params,
                headers: {
                    accountIds: accountId,
                },
            })
            .pipe(
                map((response) => response.data),
                catchError((error) => {
                    return throwError(() => error);
                }),
            );
    }

    /**
     * Thumbnails for all active attachments of a task (PT-16085 F3): presigned S3 URLs for direct
     * <img src> rendering; thumbnailUrl is null for types without a preview and for legacy rows.
     * accountId: single caller account in the task's org (G8).
     */
    getTaskAttachmentThumbnails(taskId: number, accountId: string): Observable<AttachmentThumbnailMetadata[]> {
        let params = new HttpParams();
        params = params.append("taskId", taskId);
        return this.http
            .get<APIResponse<AttachmentThumbnailMetadata[]>>(`${this.apiServerUrl}/task-attachment/download-thumbnails`, {
                params: params,
                headers: {
                    accountIds: accountId,
                },
            })
            .pipe(
                map((response) => response.data ?? []),
                catchError((error) => {
                    return throwError(() => error);
                }),
            );
    }

    /**
     * Legacy fallback (PT-16085 G3): streams the bytes of an attachment still stored in the
     * database. Only called when downloadUrl is null; removed together with backend P16.
     */
    private streamTaskAttachment(taskId: number, fileName: string, accountId: string): Observable<Blob> {
        let params = new HttpParams();
        params = params.append("taskId", taskId);
        params = params.append("fileName", fileName);
        return this.http
            .get(`${this.apiServerUrl}/task-attachment/stream-attachment`, {
                params: params,
                observe: "body",
                responseType: "blob",
                headers: {
                    accountIds: accountId,
                },
            })
            .pipe(
                catchError((error) => {
                    return throwError(() => error);
                }),
            );
    }

    deleteAllPersonalTaskAttachments(taskId: number, removerAccountId: number): Observable<any> {
        let optionIndicator: string = "all";
        let params = new HttpParams();
        params = params.append("personalTaskId", taskId);
        params = params.append("fileName", "");
        params = params.append("optionIndicator", optionIndicator);
        return this.http
            .delete(`${this.apiServerUrl}/personal-task/delete-attachments`, {
                params: params,
                headers: {
                    accountIds: removerAccountId.toString(),
                },
            })
            .pipe(
                catchError((error) => {
                    return throwError(()=>error);
                }),
            );
    }

    deleteSinglePersonalTaskAttachment(taskId: number, fileName: string, removerAccountId: number): Observable<any> {
        let optionIndicator: string = "single";
        let params = new HttpParams();
        params = params.append("personalTaskId", taskId);
        params = params.append("fileName", fileName);
        params = params.append("optionIndicator", optionIndicator);
        return this.http
            .delete(`${this.apiServerUrl}/personal-task/delete-attachments`, {
                params: params,
                headers: {
                    accountIds: removerAccountId.toString(),
                },
            })
            .pipe(
                catchError((error) => {
                    return throwError(()=>error);
                }),
            );
    }

    deleteAttachment(
        taskId: number,
        fileName: string,
        removerAccountId: number,
        optionIndicator: string = "single",
    ): Observable<any> {
        let params = new HttpParams();
        params = params.append("taskId", taskId);
        params = params.append("fileName", fileName);
        params = params.append("removerAccountId", removerAccountId);
        params = params.append("optionIndicator", optionIndicator);
        return this.http.delete(`${this.apiServerUrl}/task-attachment/delete-attachments`, {params: params}).pipe(
            catchError((error) => {
                return throwError(()=>error);
            }),
        );
    }

    deleteAllAttachments(taskId: number, removerAccountId: number, optionIndicator: string = "all"): Observable<any> {
        let params = new HttpParams();
        params = params.append("taskId", taskId);
        params = params.append("removerAccountId", removerAccountId);
        params = params.append("optionIndicator", optionIndicator);
        params = params.append("fileName", "");
        return this.http.delete(`${this.apiServerUrl}/task-attachment/delete-attachments`, {params: params}).pipe(
            catchError((error) => {
                return throwError(()=>error);
            }),
        );
    }

    getTaskHistory(taskId: number, fieldMappings: string): Observable<TaskHistory[]> {
        // Add a new parameter to the request
        let params = new HttpParams();
        params = params.append("fieldMappings", fieldMappings);
        return this.http
            .get<TaskHistory[]>(`${this.apiServerUrl}/task-history/getAllHistoryByMappings/${taskId}`, {
                params: params,
            })
            .pipe(
                catchError((error) => {
                    return throwError(()=>error);
                }),
            );
    }

    getTaskHistoryMappingFields(): Observable<TaskHistoryMappingFields[]> {
        return this.http
            .get<TaskHistoryMappingFields[]>(`${this.apiServerUrl}/task-history-mapping/getAllFieldsAndMappings`)
            .pipe(
                catchError((error) => {
                    return throwError(()=>error);
                }),
            );
    }

    getScheduledTasks(
        scheduledTasks: ScheduledTasksRequest,
        accountIds: string,
        userId: string,
    ): Observable<APIResponse<ScheduledTasksResponse[]>> {
        this.cancelPendingRequests$.next(true);
        const headers = {
            accountIds: accountIds,
            userId: userId,
        };
        return this.http
            .post<APIResponse<ScheduledTasksResponse[]>>(
                `${this.apiServerUrl}/task/getScheduledTasks`,
                scheduledTasks,
                {headers: headers},
            )
            .pipe(
                takeUntil(this.cancelPendingRequests$),
                catchError((error) => {
                    return throwError(()=>error);
                }),
            );
    }

    deleteTask(
        taskId: number,
        removerAccountId: string,
        requestPayload: WorkItemDeleteReasonsRequest,
    ): Observable<any> {
        let headers = new HttpHeaders();
        headers = headers.append("accountIds", removerAccountId);
        return this.http
            .post(`${this.apiServerUrl}/task/deleteTask/${taskId}`, requestPayload, {headers: headers})
            .pipe(
                catchError((error) => {
                    throw error;
                }),
            );
    }

    setCurrentlyActiveTask(
        task: SetCurrentlyActiveTaskRequest,
        accountIds: string,
    ): Observable<APIResponse<ScheduledTasksResponse>> {
        const headers = {
            accountIds: accountIds,
        };
        return this.http
            .post<APIResponse<ScheduledTasksResponse>>(`${this.apiServerUrl}/task/setCurrentlyActiveTasks`, task, {
                headers: headers,
            })
            .pipe(
                catchError((error) => {
                    return throwError(()=>error);
                }),
            );
    }

    addChildTask(task: SubTaskRequestModel, accountId: string): Observable<APIResponse<TaskResponse>> {
        let headers = new HttpHeaders();
        headers = headers.append("accountIds", accountId);
        return this.http
            .post<APIResponse<TaskResponse>>(`${this.apiServerUrl}/task/addChildTask`, task, {headers: headers})
            .pipe(
                catchError((error) => {
                    return throwError(()=>error);
                }),
            );
    }

    getUpdatedTaskPreview(
        openTaskRequestModel: OpenTaskRequestModel,
        accountIds: string,
    ): Observable<APIResponse<UpdatedTaskPreviewResponse>> {
        let headers = new HttpHeaders();
        headers = headers.append("accountIds", accountIds);
        return this.http
            .post<APIResponse<UpdatedTaskPreviewResponse>>(
                `${this.apiServerUrl}/task/getUpdatedTaskPreview`,
                openTaskRequestModel,
                {headers: headers},
            )
            .pipe(
                catchError((error) => {
                    return throwError(()=>error);
                }),
            );
    }

    getWorkFlowTypesPrioritiesAndStatuses(): Observable<APIResponse<any>> {
        const cachedItem = this.secureStorageService.getItem<any>(LocalStorageRelatedData.WORKFLOW_TYPE_PRIORITY_AND_STATUS);
        const cachedResponse = cachedItem ? cachedItem : null;
        if (
            cachedResponse &&
            new Date().getTime() - new Date(cachedResponse.timestamp).getTime() < 7 * 24 * 60 * 60 * 1000
        ) {
            return of(cachedResponse);
        }

        // Otherwise, fetch fresh data
        return this.http.get<any>(`${this.apiServerUrl}/workflowType/getAllWorkflowsAndPriorities`).pipe(
            map((response) => {
                // Filter out the workflow types
                response.data.workflowType = response.data.workflowType?.filter((workflowType: any) =>
                    this.allowedWorkflowTypeIds.includes(workflowType.workflowTypeId),
                );
                return response;
            }),
            tap((data) => {
                // Cache data with current timestamp
                this.secureStorageService.setItem(LocalStorageRelatedData.WORKFLOW_TYPE_PRIORITY_AND_STATUS, data);
            }),
            catchError((error) => {
                console.log(error);
                return throwError(()=>error);
            }),
        );
    }

    downloadTaskMasterDataToExport(statsRequest: StatsRequest, pageNumber: number = 0, pageSize: number = 25) {
        // set header to accept the application/octet-stream
        let headers = new HttpHeaders().set("Accept", "application/octet-stream");
        return this.http
            .post(`${this.apiServerUrl}/task/exportToCSV?pageNumber=${pageNumber}&pageSize=${pageSize}`, statsRequest, {
                headers,
                responseType: "blob",
            })
            .pipe(
                catchError((error) => {
                    return throwError(()=>error);
                }),
            );
    }

    getAllEnvironmentSeverityResolution(
        accountIds: string,
        entityTypeId: number,
        entityId: number
    ): Observable<APIResponse<GetAllEnvironmentSeverityResolution>> {
        return this.http
            .get<APIResponse<GetAllEnvironmentSeverityResolution>>(
                `${this.apiServerUrl}/task/getAllEnvironmentSeverityResolution/${entityTypeId}/${entityId}`,
                {
                    headers: {
                        accountIds: accountIds,
                    },
                },
            )
            .pipe(
                catchError((error) => {
                    return throwError(() => error);
                }),
            );
    }

    getStoredViewTaskApiResponse() {
        return this.storedViewTaskApiResponse;
    }

    setViewTaskApiResponse(response: OrganizationDashboardStructure[] | null) {
        this.storedViewTaskApiResponse = response;
    }

    async openPersonalCreateTaskDialog() {
        const {PersonalCreateTaskComponent} = await import("../modules/task-management/personal-task/personal-create-task/personal-create-task.component");
        this.dialog
            .open(PersonalCreateTaskComponent, {
                width: "50%",
            })
            .afterOpened()
            .subscribe({
                next: () => {
                },
            });
    }

    addPersonalTask(
        task: PersonalCreateTaskRequest,
        accountIds: string,
    ): Observable<APIResponse<PersonalTaskResponse>> {
        return this.http
            .post<APIResponse<PersonalTaskResponse>>(`${this.apiServerUrl}/personal-task/addTask`, task, {
                headers: {
                    accountIds: accountIds,
                },
            })
            .pipe(
                catchError((error) => {
                    return throwError(()=>error);
                }),
            );
    }

    //     personal-task/getPersonalTask/{personalTaskNumber}
    getPersonalTaskByTaskNumber(
        personalTaskNumber: number,
        accountIds: string,
    ): Observable<APIResponse<PersonalTaskResponse>> {
        return this.http
            .get<APIResponse<PersonalTaskResponse>>(
                `${this.apiServerUrl}/personal-task/getPersonalTask/${personalTaskNumber}`,
                {
                    headers: {
                        accountIds: accountIds,
                    },
                },
            )
            .pipe(
                catchError((error) => {
                    return throwError(()=>error);
                }),
            );
    }

    // /personal-task/updateTask
    updatePersonalTask(
        task: PersonalUpdateTaskRequest,
        accountIds: string,
    ): Observable<APIResponse<PersonalTaskResponse>> {
        return this.http
            .post<APIResponse<PersonalTaskResponse>>(`${this.apiServerUrl}/personal-task/updateTask`, task, {
                headers: {
                    accountIds: accountIds,
                },
            })
            .pipe(
                catchError((error) => {
                    return throwError(()=>error);
                }),
            );
    }

    updateBugDetails(request: UpdateBugDetailsRequest, accountIds: string): Observable<APIResponse<string>> {
        return this.http
            .post<APIResponse<string>>(`${this.apiServerUrl}/task/updateBugDetails`, request, {
                headers: {
                    accountIds: accountIds,
                },
            })
            .pipe(
                catchError((error) => {
                    return throwError(()=>error);
                }),
            );
    }

    deletePersonalTask(personalTaskId: number, removerAccountId: string): Observable<any> {
        let headers = new HttpHeaders();
        headers = headers.append("accountIds", removerAccountId);
        return this.http
            .delete(`${this.apiServerUrl}/personal-task/deletePersonalTask/${personalTaskId}`, {headers: headers})
            .pipe(
                catchError((error) => {
                    throw error;
                }),
            );
    }

    deleteTaskInBulk(taskIds: BulkDeleteTaskOrAskStatusRequest): Observable<APIResponse<BulkDeleteTasksResponse>> {
        return this.http
            .post<APIResponse<BulkDeleteTasksResponse>>(`${this.apiServerUrl}/task/deleteTasks`, taskIds)
            .pipe(
                catchError((error) => {
                    return throwError(()=>error);
                }),
            );
    }

    askForTasksStatus(taskIds: BulkDeleteTaskOrAskStatusRequest): Observable<APIResponse<string>> {
        return this.http.post<APIResponse<string>>(`${this.apiServerUrl}/task/askForStatus`, taskIds).pipe(
            catchError((error) => {
                return throwError(()=>error);
            }),
        );
    }

    // /api/task/updateTaskFields
    updateTaskFields(request: UpdateTaskFieldsInBulkRequest, accountIds: string): Observable<APIResponse<string>> {
        return this.http
            .post<APIResponse<string>>(`${this.apiServerUrl}/task/updateTaskFields`, request, {
                headers: {
                    accountIds: accountIds,
                },
            })
            .pipe(
                catchError((error) => {
                    console.log("backend throwing error")
                    return throwError(()=>error);
                }),
            );
    }

    validateRelation(request: ValidateWorkItemRelation, accountIds: string) {
        return this.http
            .post<APIResponse<ValidatedWorkItemResponse>>(`${this.apiServerUrl}/task/validateRelation`, request, {
                headers: {
                    accountIds: accountIds,
                },
            })
            .pipe(
                catchError((error) => {
                    return throwError(()=>error);
                }),
            );
    }

    private uploadPersonalTaskAttachment(formData: FormData, taskId: number, userAccountId: number) {
        if (this.toastr) {
            this.toastr.info("Uploading Files...", "File Upload", {
                disableTimeOut: true,
                tapToDismiss: false,
            });
        }
        const uploadedFiles = formData.getAll("files") as File[];
        const totalBytes = uploadedFiles.reduce((sum, file) => sum + file.size, 0);

        return this.http
            .post<APIResponse<UploadAttachmentTaskResponse>>(
                `${this.apiServerUrl}/personal-task/upload-attachments`,
                formData,
                {
                    params: {
                        personalTaskId: taskId.toString(),
                    },
                    headers: {
                        accountIds: userAccountId.toString(),
                        timeout: String(FileUploadTimeoutConstants.forPayload(totalBytes)),
                    },
                },
            )
            .pipe(
                catchError((error) => {
                    return throwError(()=>error);
                }),
                finalize(() => {
                    if (this.toastr) {
                        this.toastr.clear();
                    }
                }),
            );
    }

    getEpicsForListing(teamId: number, accountIds: string): Observable<APIResponse<EpicsForListingResponse>> {
        return this.http
            .get<APIResponse<EpicsForListingResponse>>(`${this.apiServerUrl}/epic/getEpicForListing?teamId=${teamId}`, {
                headers: {
                    accountIds: accountIds,
                },
            })
            .pipe(
                catchError((error) => {
                    return throwError(()=>error);
                }),
            );
    }

    getEpicsForListingFilters(
        entityTypeId: number,
        entityId: number,
        accountIds: string,
    ): Observable<APIResponse<EpicsForListingResponse>> {
        return this.http
            .get<APIResponse<EpicsForListingResponse>>(
                `${this.apiServerUrl}/epic/getEpicForListingInFilter?entityTypeId=${entityTypeId}&entityId=${entityId}`,
                {
                    headers: {
                        accountIds: accountIds,
                    },
                },
            )
            .pipe(
                catchError((error) => {
                    return throwError(()=>error);
                }),
            );
    }

    bulkUpdateTasksToSprint(request: UpdateTaskFieldsInBulkSprintRequest, accountIds: string) {
        return this.http
            .post<APIResponse<UpdateTaskWithSprintResponse>>(
                `${this.apiServerUrl}/sprint/bulkMoveTaskFromSprint`,
                request,
                {
                    headers: {
                        accountIds: accountIds,
                    },
                },
            )
            .pipe(
                catchError((error) => {
                    return throwError(()=>error);
                }),
            );
    }
}
